import { useEffect, useMemo, useRef, useState } from "react";
import { ConfigProvider, Button, Form, Input, Select, Modal, Spin, Empty, Pagination, message } from "antd";
import type { PaginationProps } from "antd";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { publicPath } from "@/utils/base";
import { appNameReg, packageNameReg } from "@/utils/constant";
import { getReleaseDetail, getType, isCustomTypeApp } from "@/utils";
import SelectWithAll from "@/components/SelectWithAll";
import api from "@/api";
import "./index.less";
import dataJson from "@/../public/data.json";
import { cloneDeep, isEqual } from "lodash-es";
import { useWatch } from "antd/es/form/Form";
const prefix = "appList";

/** 暂时屏蔽：与工作台入口一致，类型列表不展示 IDE 插件（PSO） */
const HIDDEN_APP_TYPE_KEYS = ["PSO"];

function AppList() {
  const { app_list, req_err, create, two_bars, app_detail, release, add_create } = dataJson;
  const typeObj: any = app_detail.type_obj;
  const filterFormJson = app_list.filter_form;
  const addFormJson = app_list.add_form;

  // const [insider, setInsider] = useState<boolean | null>(null); //是否内部人员：true是，false不是
  const [typeOptions, setTypeOptions] = useState<any[]>([]);
  const [kcsTypeOptions, setKcsTypeOptions] = useState<any[]>([]); // 当type为Linux时，新增软件包格式选择
  const [addOpen, setAddOpen] = useState(false);
  const [addOpenLoading, setAddFormLoading] = useState(false);
  const [pageData, setPageData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const typeFilterItem = useRef<any>({});

  const [filterForm] = Form.useForm();

  const [addForm] = Form.useForm();

  const { type } = useParams();
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.userData);

  const pageInit = {
    pageSize: 20,
    pageNo: 1,
  };
  const pageNo = useRef<number>(pageInit.pageNo);
  const pagesize = useRef<number>(pageInit.pageSize);
  const currentType = useRef<any>();
  const defaultTypeKeysRef = useRef<string[]>([]);

  const onFilter = () => {
    pageNo.current = pageInit.pageNo;
    pagesize.current = pageInit.pageSize;
    getPageData();
  };

  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    pagesize.current = pageSize;
  };

  const getPageData = async (opts?: { typeKeys?: string[] }) => {
    try {
      setLoading(true);
      const filterFormValues = await filterForm.getFieldsValue();
      const mergedTypeKeys =
        opts?.typeKeys ??
        (Array.isArray(filterFormValues.type) && filterFormValues.type.length > 0
          ? filterFormValues.type
          : undefined) ??
        (defaultTypeKeysRef.current.length > 0 ? defaultTypeKeysRef.current : undefined);
      const firstTypeKey = Array.isArray(mergedTypeKeys) ? mergedTypeKeys[0] : mergedTypeKeys;
      typeFilterItem.current = typeObj[firstTypeKey];

      const res: any = await api.getAllPage({
        page: pageNo.current,
        limit: pagesize.current,
        keyword: filterFormValues.name,
        typeKeys: mergedTypeKeys,
        showModes: filterFormValues.status,
      });
      if (res.code === 200) {
        const dataCopy = cloneDeep(res.data.datas);
        const list = dataCopy.map((item: any) => {
          item.iconImg = item?.resources?.find((ele: any) => ele.type == "ICON")?.url;
          const showModeItem = release.show_mode_arr?.find((ele: any) => ele.value === item.showMode);

          item.title = showModeItem?.title;
          item.textColor = showModeItem?.textColor;
          item.bgColor = showModeItem?.bgColor;
          item.typeLable = typeObj[item.typeKey]?.label;
          item.typeBgColor = typeObj[getType(item.typeKey)]?.bgColor;
          return item;
        });

        setPageData(list);
        setTotal(Number(res?.data?.total));
        setLoading(false);
      } else {
        setLoading(false);
        message.error(res.msg || req_err);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
      setPageData([]);
      setTotal(0);
      setLoading(false);
    }
  };

  const validateName = (_: any, value: any) => {
    if (value) {
      return api.allRepeatName({ name: value }).then((resp: any) => {
        if (resp.code === 200) {
          if (resp.data?.exist) {
            return Promise.reject(new Error(app_list.name_already));
          }
        } else {
          return Promise.resolve();
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const validatePackageName = (_: any, value: any) => {
    if (value) {
      return api.allRepeatPackageName({ packageName: value }).then((resp: any) => {
        if (resp.code === 200) {
          if (resp.data?.exist) {
            return Promise.reject(new Error(app_list.packagename_already));
          }
        } else {
          return Promise.resolve();
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const showAddModal = () => {
    setAddOpen(true);
  };

  const handleAddOpenOk = async () => {
    try {
      const addValues = await addForm.validateFields();
      setAddFormLoading(true);
      let reqMethod = "";
      if (isCustomTypeApp(addValues?.type)) {
        switch (addValues?.type) {
          case "TOOLBOX":
          case "PSO":
            reqMethod = "reqAddApp";
            break;
          default:
            reqMethod = "allAdd";
            break;
        }
      } else {
        reqMethod = "reqAddApp";
      }
      const res: any = await api[reqMethod]({
        name: addValues.name,
        packageName: addValues.packageName,
        type: currentType.current?.type,
        subType: addValues.subType ? [addValues.subType] : currentType.current?.subType,
        typeKeys: addValues?.type,
      });
      if (res.code === 200) {
        message.success(app_list.creat_success);
        setAddOpen(false);
        setAddFormLoading(false);
        if (type === "analysis") {
          navigate(`${publicPath}/app_detail/distribution_board/${addValues?.type}/${res.data.id}`);
        } else {
          navigate(`${publicPath}/app_detail/info/${addValues?.type}/${res.data.id}`);
        }
      } else {
        setAddFormLoading(false);
        message.error(res.msg || req_err);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
      setAddFormLoading(false);
    }
  };

  const handleAddCancel = () => {
    setAddOpen(false);
  };

  const clickCard = (item: any) => {
    // getReleaseDetail(item.typeKey, item.id, () => {
      if (type === "analysis") {
        navigate(`${publicPath}/app_detail/distribution_board/${item.typeKey}/${item.id}`);
      } else {
        navigate(`${publicPath}/app_detail/info/${item.typeKey}/${item.id}`);
      }
    // });
  };

  const getAllTypes = async () => {
    const res: any = await api.getAllTypes();
    if (res.code === 200) {
      const tabsList = res.data
        .filter((item: any) => {
          const t = String(item.type || "").toUpperCase();
          return item.show && !HIDDEN_APP_TYPE_KEYS.includes(item.key) && (t === "APP" || t === "ATOMIC");
        })
        .map((item: any) => {
          return {
            key: item.key,
            label: item.label,
            permissions:
              item?.permissions ||
              app_detail.type_obj[item.key as keyof typeof app_detail.type_obj]?.permissions ||
              "developer-platform:app-acs:save",
            subType: item.subType,
            type: item.type,
          };
        });
      // 根据权限判断是否显示内部人员的tab
      // const appTabs = tabsList.filter(
      //   (item: any) => userData?.permissions[0] === "*:*:*" || userData?.permissions.includes(item.permissions)
      // );
      const typeOptionMap = tabsList.map((item: any) => {
        return { value: item.key, label: item.label, subType: item.subType, type: item.type };
      });
      setTypeOptions(typeOptionMap);

      const kcs = tabsList.find((item: any) => item.key === "KCS");
      const options =
        kcs?.subType?.map((_item: string) => {
          return {
            label: _item,
            value: _item,
          };
        }) || [];
      setKcsTypeOptions(options);

      return {
        tabsList,
        typeKeys: tabsList.map((t: any) => t.key),
      };
    }
    return { appTabs: [], typeKeys: [] as string[] };
  };
  useEffect(() => {
    if (JSON.stringify(userData) !== "{}") {
      (async () => {
        const { typeKeys } = await getAllTypes();
        defaultTypeKeysRef.current = typeKeys;
        getPageData({ typeKeys });
      })();
    }
  }, [userData]);

  const typeValue = useWatch("type", addForm);

  useEffect(() => {
    const findType = typeOptions.find((item: any) => item.value === typeValue);
    if (findType) {
      currentType.current = findType;
    }
  }, [typeValue, typeOptions]);

  const newPageData = useMemo(() => {
    // 边界条件：数据未加载完成，返回原pageData（保持引用）
    if (!pageData?.length || !typeOptions?.length) {
      return pageData;
    }

    // 第一步：构建Map，优化查找性能
    const typeOptionMap = new Map(typeOptions.map((option) => [option.value, option]));

    // 第二步：不可变处理数据，仅更新需要补充typeLable的项
    const _pageData = pageData.map((item) => {
      // 已存在且匹配正确，直接返回原项（减少新对象生成）
      if (item.typeLable) {
        return item;
      }
      return {
        ...item,
        typeLable: typeOptionMap.get(item.typeKey)?.label || null,
      };
    });

    if (isEqual(_pageData, pageData)) {
      return pageData;
    }

    return _pageData;
  }, [pageData, typeOptions]);

  return (
    <div className={prefix}>
      <div className={`${prefix}-top`}>
        <Form
          form={filterForm}
          layout="inline"
          initialValues={{
            status: filterFormJson.status_options.map((item) => item.value),
          }}
        >
          <Form.Item
            name="name"
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
            style={{ marginRight: 56 }}
          >
            <Input
              placeholder={filterFormJson.name_placeholder}
              onPressEnter={onFilter}
              maxLength={150}
              style={{ width: 296 }}
            />
          </Form.Item>
          <Form.Item name="type" label={filterFormJson.type_label} style={{ marginRight: 56 }}>
            <Select
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              options={typeOptions}
              placeholder={filterFormJson.type_placeholder}
              onChange={onFilter}
              style={{ width: 296 }}
            />
          </Form.Item>
          <Form.Item name="status" label={filterFormJson.status_label}>
            <SelectWithAll
              options={filterFormJson.status_options}
              placeholder={filterFormJson.status_placeholder}
              onChange={onFilter}
              style={{ width: 296 }}
              value={filterForm.getFieldValue("status")}
            />
          </Form.Item>
        </Form>
        <ConfigProvider
          theme={{
            components: {
              Button: {
                borderRadius: 0,
                paddingInline: 40,
                paddingBlock: 8,
              },
            },
          }}
        >
          <Button type="primary" onClick={showAddModal}>
            新建
          </Button>
        </ConfigProvider>
      </div>
      <Spin spinning={loading} size="large" wrapperClassName={`${prefix}-spin`}>
        {total === 0 ? (
          <Empty />
        ) : (
          <div className={`${prefix}-content`}>
            {newPageData.map((item: any) => {
              return (
                <div
                  key={item.id}
                  onClick={() => {
                    clickCard(item);
                  }}
                  className={`${prefix}-content-item`}
                >
                  <div
                    className={`${prefix}-content-status`}
                    style={{ color: item?.textColor, backgroundColor: item?.bgColor }}
                  >
                    {item.title}
                  </div>
                  <div className={`${prefix}-content-type`} style={{ backgroundColor: item?.typeBgColor }}>
                    {item.typeLable}
                  </div>
                  <div className={`${prefix}-content-inner`}>
                    <img src={item?.iconImg ? item?.iconImg : app_list.init_img} alt="" />
                    <div>
                      <div title={item?.name} className={`${prefix}-content-title one-line-ellipsis`}>
                        {item?.name || two_bars}
                      </div>
                      <div>{app_detail.package_name}</div>
                      <div title={item?.packageName || ""} className={`${prefix}-content-package two-line-ellipsis`}>
                        {item?.packageName || two_bars}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Spin>
      <Pagination
        pageSize={pagesize.current}
        current={pageNo.current}
        total={total}
        showSizeChanger
        showQuickJumper
        showTotal={(total) => `共 ${total} 条`}
        align="end"
        className={`${prefix}_page`}
        onShowSizeChange={onShowSizeChange}
        onChange={(page: number) => {
          pageNo.current = page;
          getPageData();
        }}
      />
      <Modal
        title={create}
        centered
        destroyOnClose
        confirmLoading={addOpenLoading}
        open={addOpen}
        okText={add_create}
        onOk={handleAddOpenOk}
        onCancel={handleAddCancel}
        width={328}
      >
        <Form form={addForm} layout="vertical" clearOnDestroy>
          <Form.Item
            name="type"
            label={addFormJson.type_label}
            rules={[{ required: true, message: addFormJson.type_placeholder }]}
          >
            <Select options={typeOptions} placeholder={addFormJson.type_placeholder} />
          </Form.Item>
          {typeValue === "KCS" && (
            <Form.Item
              name="subType"
              label="软件包格式"
              validateDebounce={500}
              rules={[{ required: true, message: addFormJson.subType_placeholder }]}
            >
              <Select options={kcsTypeOptions} placeholder={addFormJson.type_placeholder} />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label={addFormJson.name_label}
            validateDebounce={500}
            rules={[
              { required: true, message: addFormJson.name_placeholder },
              {
                pattern: appNameReg,
                message: app_detail.name_validator,
              },
              { validator: validateName },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input showCount maxLength={30} placeholder={addFormJson.name_placeholder} />
          </Form.Item>
          <Form.Item
            name="packageName"
            label={addFormJson.packageName_label}
            validateDebounce={500}
            rules={[
              { required: true, message: addFormJson.packageName_placeholder },
              {
                pattern: packageNameReg,
                message: app_detail.package_name_validator,
              },
              { validator: validatePackageName },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input showCount maxLength={127} placeholder={addFormJson.packageName_placeholder} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AppList;
